// Keyboard shortcuts (F-15) with user overrides (Phase 3, stored in settings and team presets).

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { $, anyModalOpen, closeModal, openModal, toast } from './dom';
import { t } from '../i18n';

export interface ShortcutAction {
  id: string;
  keys: string[];
  labelKey: string;
  run: (app: App) => void;
}

const HOLD_MS = 200;

/** Normalised key string, e.g. "Ctrl+Shift+Z", "Alt+ArrowLeft", "Space", "J", "[". */
export function keyString(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  let k = e.key;
  if (k === ' ') k = 'Space';
  else if (k.length === 1) k = k.toUpperCase();
  // Shift is implied by printable symbols such as "?", "+"; keep it for named keys and letters.
  if (e.shiftKey && (k.length > 1 || /[A-Z0-9]/.test(k))) parts.push('Shift');
  if (e.code.startsWith('Digit') && e.shiftKey) k = e.code.slice(5);
  parts.push(k);
  return parts.join('+');
}

export function actions(): ShortcutAction[] {
  const L = (app: App) => app.project.layout;
  return [
    { id: 'play', keys: ['Space'], labelKey: 'help.k.play', run: (a) => a.player.toggle() },
    { id: 'stepBack', keys: ['ArrowLeft'], labelKey: 'help.k.stepBack', run: (a) => a.player.step(-1) },
    { id: 'stepFwd', keys: ['ArrowRight'], labelKey: 'help.k.stepFwd', run: (a) => a.player.step(1) },
    { id: 'back10', keys: ['Shift+ArrowLeft'], labelKey: 'help.k.back10', run: (a) => a.player.step(-10) },
    { id: 'fwd10', keys: ['Shift+ArrowRight'], labelKey: 'help.k.fwd10', run: (a) => a.player.step(10) },
    { id: 'start', keys: ['Home'], labelKey: 'help.k.start', run: (a) => a.player.seek(a.player.bounds().lo) },
    { id: 'end', keys: ['End'], labelKey: 'help.k.end', run: (a) => a.player.seek(a.player.bounds().hi) },
    { id: 'shuttleRev', keys: ['J'], labelKey: 'help.k.j', run: (a) => a.player.shuttle(-1) },
    { id: 'shuttleStop', keys: ['K'], labelKey: 'help.k.k', run: (a) => a.player.shuttle(0) },
    { id: 'shuttleFwd', keys: ['L'], labelKey: 'help.k.l', run: (a) => a.player.shuttle(1) },
    { id: 'setIn', keys: ['I'], labelKey: 'help.k.in', run: (a) => a.setIn() },
    { id: 'setOut', keys: ['O'], labelKey: 'help.k.out', run: (a) => a.setOut() },
    { id: 'offDown', keys: ['['], labelKey: 'help.k.offDown', run: (a) => a.setOffset(L(a).selectedB, -1) },
    { id: 'offUp', keys: [']'], labelKey: 'help.k.offUp', run: (a) => a.setOffset(L(a).selectedB, 1) },
    {
      id: 'goto',
      keys: ['Ctrl+G'],
      labelKey: 'help.k.goto',
      run: () => {
        openModal('#gotoModal');
        const i = $<HTMLInputElement>('#gotoInput');
        i.value = '';
        setTimeout(() => i.focus(), 0);
      },
    },
    { id: 'layout', keys: ['Q'], labelKey: 'help.k.layout', run: (a) => a.cycleLayout() },
    { id: 'mode', keys: ['W'], labelKey: 'help.k.mode', run: (a) => a.cycleMode() },
    { id: 'diff', keys: ['D'], labelKey: 'help.k.diff', run: (a) => a.toggleDifference() },
    { id: 'fit', keys: ['F'], labelKey: 'help.k.fit', run: (a) => a.setViewZoom('fit') },
    { id: 'actual', keys: ['0'], labelKey: 'help.k.actual', run: (a) => a.setViewZoom(100) },
    { id: 'fullscreen', keys: ['Shift+F'], labelKey: 'help.k.fullscreen', run: () => toggleFullscreen() },
    { id: 'crosshair', keys: ['C'], labelKey: 'help.k.crosshair', run: (a) => a.toggleCrosshair() },
    { id: 'zoomIn', keys: ['+', '='], labelKey: 'help.k.zoomIn', run: (a) => a.zoomStep(1) },
    { id: 'zoomOut', keys: ['-', '_'], labelKey: 'help.k.zoomOut', run: (a) => a.zoomStep(-1) },
    { id: 'crossLeft', keys: ['Alt+ArrowLeft'], labelKey: 'help.k.crossLeft', run: (a) => a.nudgeCrosshair(-1, 0) },
    { id: 'crossRight', keys: ['Alt+ArrowRight'], labelKey: 'help.k.crossRight', run: (a) => a.nudgeCrosshair(1, 0) },
    { id: 'crossUp', keys: ['Alt+ArrowUp'], labelKey: 'help.k.crossUp', run: (a) => a.nudgeCrosshair(0, -1) },
    { id: 'crossDown', keys: ['Alt+ArrowDown'], labelKey: 'help.k.crossDown', run: (a) => a.nudgeCrosshair(0, 1) },
    { id: 'crossLeft10', keys: ['Alt+Shift+ArrowLeft'], labelKey: 'help.k.crossLeft10', run: (a) => a.nudgeCrosshair(-10, 0) },
    { id: 'crossRight10', keys: ['Alt+Shift+ArrowRight'], labelKey: 'help.k.crossRight10', run: (a) => a.nudgeCrosshair(10, 0) },
    { id: 'crossUp10', keys: ['Alt+Shift+ArrowUp'], labelKey: 'help.k.crossUp10', run: (a) => a.nudgeCrosshair(0, -10) },
    { id: 'crossDown10', keys: ['Alt+Shift+ArrowDown'], labelKey: 'help.k.crossDown10', run: (a) => a.nudgeCrosshair(0, 10) },
    { id: 'texture', keys: ['G'], labelKey: 'help.k.texture', run: (a) => a.store.project((p) => (p.layout.textureView = !p.layout.textureView), { undo: false }) },
    { id: 'adjust', keys: ['V'], labelKey: 'help.k.adjust', run: (a) => a.store.project((p) => (p.viewAdjustment.enabled = !p.viewAdjustment.enabled)) },
    { id: 'preset', keys: ['Shift+V'], labelKey: 'help.k.preset', run: (a) => a.cyclePreset() },
    { id: 'save', keys: ['Ctrl+S'], labelKey: 'help.k.save', run: (a) => void a.saveProject() },
    { id: 'undo', keys: ['Ctrl+Z'], labelKey: 'help.k.undo', run: (a) => a.store.undo() },
    { id: 'redo', keys: ['Ctrl+Shift+Z', 'Ctrl+Y'], labelKey: 'help.k.redo', run: (a) => a.store.redo() },
    { id: 'help', keys: ['?'], labelKey: 'help.k.help', run: () => toggleModal('#helpModal') },
  ];
}

function toggleModal(id: string): void {
  const m = $(id);
  m.hidden = !m.hidden;
}

export function toggleFullscreen(): void {
  const body = document.body;
  if (body.classList.contains('bare')) {
    body.classList.remove('bare');
    if (document.fullscreenElement) void document.exitFullscreen();
  } else {
    body.classList.add('bare');
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
  }
}

/** Effective key → action map after user overrides. */
export function keymap(app: App): Map<string, ShortcutAction> {
  const map = new Map<string, ShortcutAction>();
  const custom = app.state.settings.shortcuts ?? {};
  for (const a of actions()) {
    const keys = custom[a.id] ? [custom[a.id]] : a.keys;
    for (const k of keys) map.set(k, a);
  }
  return map;
}

export function initKeyboard(app: App): void {
  const held = new Map<string, { at: number; id: SourceId; timer: number }>();
  const numberIds: SourceId[] = ['A', 'B1', 'B2', 'B3'];

  window.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement;
    const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
    if (e.key === 'Escape') {
      if (anyModalOpen()) {
        document.querySelectorAll<HTMLElement>('.modal').forEach((m) => (m.hidden = true));
        return;
      }
      // F-03.11: leaves two-point alignment.
      if (app.state.ui.alignPick && !typing) {
        app.cancelPointAlign();
        return;
      }
      if (typing) {
        target.blur();
        return;
      }
      if (document.body.classList.contains('bare')) {
        toggleFullscreen();
        return;
      }
      if (app.state.ui.alignOpen) {
        app.store.ui((u) => (u.alignOpen = false));
        return;
      }
      return;
    }
    const ks = keyString(e);
    // Ctrl/Cmd shortcuts work even inside fields, except when editing text (undo there is the field's own).
    if (typing && !(e.ctrlKey || e.metaKey)) return;
    if (typing && (ks === 'Ctrl+Z' || ks === 'Ctrl+Shift+Z' || ks === 'Ctrl+Y')) return;
    if (anyModalOpen() && !(e.ctrlKey || e.metaKey)) {
      if (ks === 'Enter' && !$('#gotoModal').hidden) $('#gotoGo').click();
      return;
    }
    // Number keys: short press selects, hold shows the source full-screen temporarily (F-15 table).
    if (/^Digit[1-4]$/.test(e.code) && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
      e.preventDefault();
      if (e.repeat) return;
      const id = numberIds[Number(e.code.slice(5)) - 1];
      const timer = window.setTimeout(() => {
        if (app.sources.has(id) && app.project.layout.layout !== 'single') app.store.ui((u) => (u.tempFull = id));
      }, HOLD_MS);
      held.set(e.code, { at: performance.now(), id, timer });
      return;
    }
    const act = keymap(app).get(ks);
    if (!act) return;
    e.preventDefault();
    act.run(app);
  });

  window.addEventListener('keyup', (e) => {
    const h = held.get(e.code);
    if (!h) return;
    held.delete(e.code);
    clearTimeout(h.timer);
    const long = performance.now() - h.at >= HOLD_MS;
    if (app.state.ui.tempFull) app.store.ui((u) => (u.tempFull = null));
    if (long) return;
    const layout = app.project.layout.layout;
    if (layout === 'single') {
      if (app.sources.has(h.id)) app.singleSource(h.id);
    } else if (h.id !== 'A' && (layout === 'side-by-side' || layout === 'side-by-side-zoom' || layout === 'focus')) {
      if (app.sources.has(h.id)) app.selectB(h.id);
    }
  });

  window.addEventListener('blur', () => {
    // Key-up never arrives after switching windows: drop pending holds too.
    for (const h of held.values()) clearTimeout(h.timer);
    held.clear();
    if (app.state.ui.tempFull) app.store.ui((u) => (u.tempFull = null));
  });

  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && document.body.classList.contains('bare')) document.body.classList.remove('bare');
  });

  $('#gotoGo').addEventListener('click', () => {
    const v = $<HTMLInputElement>('#gotoInput').value;
    if (app.gotoText(v)) closeModal('#gotoModal');
    else toast(t('goto.invalid'));
  });
}
